import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { LightModal } from '@repo/ui/components/Modal/LightModal';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@repo/ui/components/Form';
import Input from '@repo/ui/components/Input';
import Button from '@repo/ui/components/Button';
import { Spinner } from '@repo/ui/components/Spinner';
import { CheckCircle2, Circle, KeyRound } from 'lucide-react';
import { zodResolver } from '../../utils/zodResolver';
import { useLinkPassword } from '../../hooks/auth/usePasswordSync';
import { passwordSchema, passwordRequirements } from '@repo/shared/dtos/PasswordDto';
import { PASSWORD_SYNC_COPY } from './constants';

const schema = z
  .object({
    password: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine(d => d.password === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type FormData = z.infer<typeof schema>;

interface PasswordSyncModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function PasswordSyncModal({ isOpen, onClose, onSuccess }: PasswordSyncModalProps) {
  const { mutateAsync: linkPassword, isPending } = useLinkPassword();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [synced, setSynced] = useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: { password: '', confirmPassword: '' },
  });

  const password = form.watch('password');

  const onSubmit = async (data: FormData) => {
    setErrorMessage(null);
    try {
      await linkPassword(data.password);
      form.reset();
      setSynced(true);
    } catch (err: unknown) {
      setErrorMessage(err instanceof Error ? err.message : PASSWORD_SYNC_COPY.modal.errorFallback);
    }
  };

  const handleClose = () => {
    form.reset();
    setErrorMessage(null);
    setSynced(false);
    onClose();
  };

  const handleDone = () => {
    onSuccess();
    handleClose();
  };

  if (synced) {
    return (
      <LightModal
        isOpen={isOpen}
        title={PASSWORD_SYNC_COPY.success.title}
        onClose={handleDone}
        className="max-w-md"
        showCloseButton={false}
      >
        <div className="space-y-6 py-2">
          <div className="flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-green-100">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
          </div>
          <div className="text-center">
            <p className="text-base font-semibold text-gray-800">
              {PASSWORD_SYNC_COPY.success.heading}
            </p>
            <p className="mt-1.5 text-sm text-gray-500">{PASSWORD_SYNC_COPY.success.body}</p>
          </div>
          <Button onClick={handleDone} className="w-full">
            {PASSWORD_SYNC_COPY.success.doneButton}
          </Button>
        </div>
      </LightModal>
    );
  }

  return (
    <LightModal
      isOpen={isOpen}
      title={PASSWORD_SYNC_COPY.modal.title}
      onClose={handleClose}
      className="max-w-md"
    >
      <div className="space-y-5">
        <div className="flex items-start gap-3 rounded-lg bg-blue-50 p-3.5 ring-1 ring-blue-100">
          <KeyRound className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" aria-hidden="true" />
          <p className="text-sm text-blue-800">{PASSWORD_SYNC_COPY.modal.infoBanner}</p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{PASSWORD_SYNC_COPY.modal.passwordLabel}</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      autoComplete="new-password"
                      placeholder={PASSWORD_SYNC_COPY.modal.passwordPlaceholder}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="rounded-lg bg-gray-50 p-3" aria-live="polite" aria-atomic="false">
              <div className="space-y-1.5">
                {passwordRequirements.map(req => {
                  const met = req.check(password);
                  return (
                    <div
                      key={req.label}
                      className={`flex items-center gap-2 text-xs transition-colors ${met ? 'text-green-600' : 'text-gray-400'}`}
                    >
                      {met ? (
                        <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                      ) : (
                        <Circle className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                      )}
                      {req.label}
                    </div>
                  );
                })}
              </div>
            </div>

            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{PASSWORD_SYNC_COPY.modal.confirmLabel}</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder={PASSWORD_SYNC_COPY.modal.confirmPlaceholder}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {errorMessage && (
              <p className="text-sm text-red-600" role="alert">
                {errorMessage}
              </p>
            )}

            <div className="flex justify-end gap-3 pt-1">
              <Button variant="outline" onClick={handleClose} type="button">
                {PASSWORD_SYNC_COPY.modal.cancelButton}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? (
                  <span className="flex items-center gap-2">
                    <Spinner size="sm" />
                    {PASSWORD_SYNC_COPY.modal.submittingButton}
                  </span>
                ) : (
                  PASSWORD_SYNC_COPY.modal.submitButton
                )}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </LightModal>
  );
}
